<template>
  <div>
    <b-card-actions
      title="Filters"
      action-collapse
      collapsed
    >
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Created Date"
            label-for="created_at"
          >
            <AdvancedDateFilter
              :date-value="filters['created_at']"
              :opens="'right'"
              @update="updateDateRange('created_at', $event)"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Event Type"
            label-for="event_type"
          >
            <v-select
              v-model="filters['event_type']"
              :options="eventTypeOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Select Event Type"
              clearable
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Waybill Number"
            label-for="waybill_number"
          >
            <b-form-input
              id="waybill_number"
              v-model="filters['waybill_number']"
              placeholder="Filter by waybill ID"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Status"
            label-for="success"
          >
            <v-select
              v-model="filters['success']"
              :options="statusOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Select Status"
              clearable
            />
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card no-body>
      <div>
        <div class="m-2">
          <b-row class="d-sm-flex align-items-start">
            <b-col
              class="d-flex d-sm-flex flex-wrap justify-content-end align-items-xl-center mb-1 mb-md-0"
            >
              <TableButtons
                :items="rows"
                :json_fieldz="json_fields"
                :name="`Webhook Logs - Page ${page}.xls`"
                :bulkname="`Webhook Logs.xls`"
                :fetch="getWebhookLogsNoPagination"
                @refresh="onClickRefresh"
              />
            </b-col>
          </b-row>
        </div>

        <!-- table -->
        <b-overlay
          id="overlay-background"
          variant="light"
          opacity="0.30"
          blur="10px"
          rounded="sm"
        >
          <b-table
            id="webhook-logs-table"
            striped
            hover
            responsive
            :per-page="perPage"
            :items="rows"
            :fields="fields"
            show-empty
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>

            <template #cell(waybill_number)="data">
              {{ data.value }}
            </template>

            <template #cell(event_type)="data">
              {{ data.value }}
            </template>

            <template #cell(success)="data">
              <div v-if="data.value">
                <b-badge variant="light-success">
                  Success
                </b-badge>
              </div>
              <div v-else>
                <b-badge variant="light-danger">
                  Failed
                </b-badge>
              </div>
            </template>

            <template #cell(response_code)="data">
              <b-badge :variant="getResponseCodeVariant(data.value)">
                {{ data.value }}
              </b-badge>
            </template>

            <template #cell(payload)="data">
              <b-button
                v-b-modal="`payload-modal-${data.item.id}`"
                variant="outline-primary"
                size="sm"
              >
                View Payload
              </b-button>
            </template>

            <template #cell(response_body)="data">
              <b-button
                v-if="data.value"
                v-b-modal="`response-modal-${data.item.id}`"
                variant="outline-secondary"
                size="sm"
              >
                View Response
              </b-button>
              <span
                v-else
                class="text-muted"
              >No Response</span>
            </template>

            <template #cell(error)="data">
              <b-button
                v-if="data.value"
                v-b-modal="`error-modal-${data.item.id}`"
                variant="outline-danger"
                size="sm"
              >
                View Error
              </b-button>
              <span
                v-else
                class="text-muted"
              >No Error</span>
            </template>

            <template #cell(created_at)="data">
              <div>
                {{ __dateTimeFormatter(data.value) }}
              </div>
            </template>

            <template #cell(action)="data">
              <b-dropdown
                v-can="'view-webhook-logs'"
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  v-can="'view-webhook-logs'"
                  @click="onClickView(data.item)"
                >
                  <feather-icon icon="EyeIcon" />
                  View
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row cols="12">
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{
                  meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="(meta.total) ? meta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </div>
    </b-card>

    <!-- Payload Modals -->
    <b-modal
      v-for="item in rows"
      :id="`payload-modal-${item.id}`"
      :key="`payload-${item.id}`"
      :title="`Payload - ${item.waybill_number}`"
      size="lg"
      scrollable
    >
      <pre class="bg-light p-3 rounded">{{ JSON.stringify(item.payload, null, 2) }}</pre>
    </b-modal>

    <!-- Response Modals -->
    <b-modal
      v-for="item in rows"
      :id="`response-modal-${item.id}`"
      :key="`response-${item.id}`"
      :title="`Response - ${item.waybill_number}`"
      size="lg"
      scrollable
    >
      <pre class="bg-light p-3 rounded">{{ item.response_body }}</pre>
    </b-modal>

    <!-- Error Modals -->
    <b-modal
      v-for="item in rows"
      :id="`error-modal-${item.id}`"
      :key="`error-${item.id}`"
      :title="`Error - ${item.waybill_number}`"
      size="lg"
      scrollable
    >
      <pre class="bg-light p-3 rounded">{{ item.error }}</pre>
    </b-modal>
    <!-- Webhook Log Detail Modal -->
    <view-webhook-log-modal ref="viewWebhookLogModal" />
  </div>
</template>

<script>
/* eslint-disable */
import {
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BPagination,
  VBModal,
  BRow,
  BOverlay,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store/index'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import TableButtons from '@/components/TableButtons/TableButtons.vue'
import TableDataFetching from '@/components/TableDataFetching.vue'
import ViewWebhookLogModal from '@/components/webhooks/ViewWebhookLogModal.vue'

const webhookLogsRepository = RepositoryFactory.get('webhookLogs')
export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    BButton,
    BPagination,
    BFormGroup,
    BFormInput,
    BCard,
    BRow,
    BCol,
    BTable,
    BOverlay,
    vSelect,
    AdvancedDateFilter,
    BCardActions,
    BBadge,
    BDropdown,
    BDropdownItem,
    FilterButtons,
    TableButtons,
    TableDataFetching,
    ViewWebhookLogModal,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return {
      // Filters Start
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
        event_type: '',
        waybill_number: '',
        success: '',
      },
      // Filters End
      total: 0,
      meta: {},
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      statusOptions: [
        { text: 'Success', value: '1' },
        { text: 'Failed', value: '0' },
      ],
      eventTypeOptions: [],
      fields: [
        {
          key: 'waybill_number',
          label: 'Waybill Number',
          sortable: true,
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'event_type',
          label: 'Event Type',
          sortable: true,
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'success',
          label: 'Status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'response_code',
          label: 'Response Code',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'payload',
          label: 'Payload',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'response_body',
          label: 'Response',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'error',
          label: 'Error',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'created_at',
          label: 'Created Date',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'action',
          label: 'Action',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],
      rows: [],
      json_fields: {
        'Waybill Number': 'waybill_number',
        'Event Type': 'event_type',
        Status: 'success',
        'Response Code': 'response_code',
        'Created Date': {
          field: 'created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
      },
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
  },
  watch: {
    page() {
      this.getWebhookLogs()
    },
    perPage() {
      this.getWebhookLogs()
    },
  },
  async mounted() {
    this.totalRows = this.rows.length
    await this.fetchWebhookConfig()
    this.getWebhookLogs()
  },
  methods: {
    async getWebhookLogs() {
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await webhookLogsRepository.getAllWebhookLogs(this.page, filterQuery, this.perPage))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getWebhookLogsNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await webhookLogsRepository.getAllWebhookLogsNoPagination(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    // REQUIRED
    applyFilters() {
      this.getWebhookLogs()
    },
    clearFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
        event_type: '',
        waybill_number: '',
        success: '',
      }
      this.$nextTick(() => {
        this.getWebhookLogs()
      })
    },
    handleChangePage(page) {
      this.page = page
      this.getWebhookLogs()
    },
    changePage(value) {
      this.perPage = value
    },
    // REQUIRED
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
        this.getWebhookLogs()
      }
    },
    onClickRefresh() {
      this.getWebhookLogs()
    },
    async fetchWebhookConfig() {
      try {
        const { data } = await webhookLogsRepository.getWebhookConfig()
        // Convert event_types to event type options
        this.eventTypeOptions = Object.entries(data.data.event_types).map(([key, value]) => ({
          text: value,
          value: key,
        }))
      } catch (e) {
        // Fallback to default options if API fails
        this.eventTypeOptions = [
          { text: 'Order Status Update', value: 'order_status_update' },
        ]
      }
    },
    getResponseCodeVariant(code) {
      if (code >= 200 && code < 300) return 'success'
      if (code >= 300 && code < 400) return 'warning'
      if (code >= 400 && code < 500) return 'danger'
      if (code >= 500) return 'danger'
      return 'secondary'
    },
    onClickView(webhookLog) {
      // Open webhook log detail modal
      this.$refs.viewWebhookLogModal.openModal(webhookLog)
    },
  },
}
</script>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
@media (max-width: 767px) {
    #webhook-logs-table thead th {
        position: sticky;
        top: 0;
    }
}

pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    max-height: 400px;
    overflow-y: auto;
}
</style>
